import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import {
  GOAL,
  GoalType,
  IMatchScoreEvent,
  IMatchSubstitution,
  OWN,
  PENALTY,
  RED,
  REDONYELLOW,
  ScorersCardsType,
  YELLOW,
} from '../../../typings/match-formations';
import { makeClassNames } from '../../../utils/styles';
import IconRedCardWithoutHand from '../../atoms/icons/icon-red-card-without-hand';
import IconRedYellowCard from '../../atoms/icons/icon-red-yellow-card';
import IconScored from '../../atoms/icons/icon-scored';
import IconStar from '../../atoms/icons/icon-star';
import IconSubstitution from '../../atoms/icons/icon-substitution';
import IconYellowCardWithoutHand from '../../atoms/icons/icon-yellow-card-without-hand';
import Text from '../../atoms/text/text';
import Tooltip from '../../atoms/tooltip/tooltip';

const classNames = () =>
  makeClassNames({
    goalAuto: 'match-formation-player--goal-aut',
    goalWrapper: 'match-formation-player--goal-wrapper',
    goalNumber: 'match-formation-player--goal-number',
  });

const cn = classNames();

/**
 * Utility function to get TopPlayer
 * @param id current player id
 * @param topPlayerId top player id
 */
export const getTopPlayer = (id: string | number | null, topPlayerId: number | undefined) =>
  id === topPlayerId ? (
    <Tooltip text={t('report.top.player')}>
      <IconStar size="xxsmall" color="red-lighter" />
    </Tooltip>
  ) : null;

/**
 * Utility function to get PlayerInOut (players in substitution)
 * @param id current player id
 * @param substitution array of match substitution events
 */
export const getPlayerInOut = (id: string | number | null, substitution: IMatchSubstitution[]): JSX.Element[] => {
  return substitution.map((sub: IMatchSubstitution) => (
    <Fragment key={`${sub.playerIdIn}-${sub.playerIdOut}`}>
      {(sub.playerIdIn === id || sub.playerIdOut === id) && (
        <Text color="medium" fontSize="16--14">
          {`${sub.minutes}'`}
        </Text>
      )}
      {sub.playerIdIn === id ? <IconSubstitution size="xsmall" color="green" /> : null}
      {sub.playerIdOut === id ? <IconSubstitution size="xsmall" color="red-light" /> : null}
    </Fragment>
  ));
};

/**
 * Utility function to get Score Card Icon
 * @param scorersCard scoreCard name
 */
const getScoreCardIcon = (scorersCard: GoalType | ScorersCardsType): JSX.Element | null => {
  switch (scorersCard) {
    case RED:
      return <IconRedCardWithoutHand size="xsmall" />;

    case YELLOW:
      return <IconYellowCardWithoutHand size="xsmall" />;

    case REDONYELLOW:
      return <IconRedYellowCard size="xsmall" />;

    /* istanbul ignore next: unreachable code */
    default:
      return null; // GoalType
  }
};

/**
 * Utility function to get Score Card
 * @param id current player id
 * @param scoresCards array of match score events
 */
export const getScoresCard = (id: string | number | null, scoresCards: IMatchScoreEvent[]): JSX.Element[] => {
  return scoresCards.map((scorersCard: IMatchScoreEvent, index: number) => (
    <Fragment key={`${scorersCard.id}-${index}`}>
      {scorersCard.id === id ? getScoreCardIcon(scorersCard.type) : null}
    </Fragment>
  ));
};

/**
 * Utility function to get Goal Icon
 * @param goal goal name
 */
const getGoalIcon = (goal: GoalType): JSX.Element | null => {
  switch (goal) {
    case OWN:
      return (
        <Fragment>
          <Text className={cn.goalAuto} color="medium" fontSize="16--14">
            {t('formation.player.autGoal')}
          </Text>
          <IconScored size="xsmall" color="red-light" />
        </Fragment>
      );

    case GOAL:
    case PENALTY:
      return <IconScored size="xsmall" />;

    /* istanbul ignore next: unreachable code */
    default:
      return null;
  }
};

const getGoalsComponent = (goals: IMatchScoreEvent[], goalType: GoalType): JSX.Element => {
  return (
    <span className={cn.goalWrapper}>
      {getGoalIcon(goalType)}
      {goals.length > 1 && <span className={cn.goalNumber}>{goals.length}</span>}
    </span>
  );
};

/**
 * Utility function to get Goals
 * @param id current player id
 * @param goals array of match goal events
 */
export const getGoals = (id: string | number | null, goals: IMatchScoreEvent[]): JSX.Element | null => {
  // Split GOALS and AUTOGOALS (different ICONS)
  const arrayGoals = goals.filter((goal: IMatchScoreEvent) => goal.id === id && goal.type !== OWN);
  const arrayAutogoals = goals.filter((goal: IMatchScoreEvent) => goal.id === id && goal.type === OWN);

  return (
    <Fragment>
      {arrayAutogoals.length > 0 && getGoalsComponent(arrayAutogoals, OWN)}
      {arrayGoals.length > 0 && getGoalsComponent(arrayGoals, GOAL)}
    </Fragment>
  );
};
